/**
 * Segmentation-study case timer pill (ENG-202).
 *
 * Shows the time that is (or will be) recorded for this case:
 *   backend total (GET .../time)
 *   + this page's events after the backend's as_of_client_ts (same gap rules)
 *   + the running gap since the last event, while it is still ≤ the idle cap.
 * Once that gap passes the cap the pill drops back to the last event and shows
 * paused: the backend won't count an idle gap. It also freezes while the tab is
 * hidden or the case is closed, and continues from the backend total after a
 * refresh. Without the backend (404 / not deployed) it shows --:--.
 */
import { useEffect, useState } from "react";
import { useCaseEventLogContext } from "../../hooks/useCaseEventLog";
import { useCaseTimeQuery, type CaseTimeResponse } from "../../hooks/useLabelingQueries";
import { caseSeconds, countedIntervals, secondsAfter } from "../../utils/caseTime";
import type { KnownCaseEvent } from "../../utils/caseEventQueue";
import { StudyTimerPill } from "./StudyTimerPill";

const CLOSE_CHANGES = new Set([
  "mark_complete",
  "unmark",
  "mark_unassessable",
  "clear_unassessable",
]);

export interface PillReading {
  seconds: number;
  paused: boolean;
}

/** Pure reading for one moment; null when the backend total isn't known. */
export function pillReading(
  data: CaseTimeResponse | undefined,
  known: readonly KnownCaseEvent[],
  hidden: boolean,
  nowMs: number,
): PillReading | null {
  if (!data) return null;
  const cap = data.idle_cap_s;
  // This page's own complete / unmark stamps are newer than the last read;
  // without them, trust the backend's markers (also right for cases completed
  // before the event log): a closed case shows the recorded total, frozen.
  const firstChange = known.findIndex((event) => CLOSE_CHANGES.has(event.type));
  if (firstChange < 0 && data.state === "complete") {
    return { seconds: data.active_seconds, paused: true };
  }
  // Read as closed, then this page unmarked: everything before that first
  // stamp was closed time (the backend has the earlier mark_complete; this
  // page doesn't), so it never counts.
  const events = data.state === "complete" ? known.slice(firstChange) : known;
  const local = caseSeconds(events, cap);
  const closed = local.closed;
  const asOf = data.as_of_client_ts ? Date.parse(data.as_of_client_ts) : Number.NEGATIVE_INFINITY;
  const live = secondsAfter(countedIntervals(events, cap), asOf);
  let tail = 0;
  if (!hidden && !closed && local.lastTs !== null) {
    const gapS = (nowMs - local.lastTs) / 1000;
    if (gapS >= 0 && gapS <= cap) {
      // Don't count time before as_of the backend may already have (other tab).
      tail = Math.max(0, (nowMs - Math.max(local.lastTs, asOf)) / 1000);
    }
  }
  return { seconds: data.active_seconds + live + tail, paused: hidden || closed || tail === 0 };
}

export function SegmentationCaseTimer({
  projectId,
  studyUid,
}: {
  projectId: string;
  /** May carry a ::job suffix; events are keyed by the bare study UID. */
  studyUid: string;
}) {
  const log = useCaseEventLogContext();
  const study = studyUid.split("::")[0];
  const { data } = useCaseTimeQuery(projectId, study, !!log);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const reading = log ? pillReading(data, log.knownEvents(study), log.isHidden(), now) : null;
  return <StudyTimerPill elapsedSeconds={reading?.seconds ?? null} paused={reading?.paused} />;
}
